import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  UserCheck, Filter, Plus, ExternalLink, Eye, EyeOff,
  AlertTriangle, ChevronDown, ChevronRight, Edit, Trash2, Search,
  MapPin, DollarSign, Building2, Phone, Mail, Globe, Lock, Copy,
  CheckCircle2, XCircle, Clock
} from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

const RegisteredAgentsManagement = ({ user }) => {
  const [providers, setProviders] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedProviders, setExpandedProviders] = useState(new Set());
  
  // Dialogs
  const [addProviderDialog, setAddProviderDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [revealedCredentials, setRevealedCredentials] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [overviewRes, providersRes] = await Promise.all([
        api.getRAOverview(),
        api.getRAProviders()
      ]);
      
      console.log('RA Data Loaded:', { overviewRes, providersRes });
      
      setOverview(overviewRes || {});
      
      // Enrich providers with their state assignments from overview
      const assignments = overviewRes?.assignments || [];
      const providers = providersRes?.providers || [];
      
      console.log('Processing:', { assignments: assignments.length, providers: providers.length });
      
      const enrichedProviders = providers.map(p => {
        const providerAssignments = assignments.filter(a => a.provider_id === p.provider_id);
        return {
          ...p,
          state_assignments: providerAssignments,
          states_covered: providerAssignments.length,
          total_annual_cost: providerAssignments.reduce((sum, a) => sum + (a.annual_fee || 0), 0)
        };
      });
      
      console.log('Enriched Providers:', enrichedProviders.length, enrichedProviders);
      setProviders(enrichedProviders);
    } catch (error) {
      console.error('Failed to load registered agents:', error);
      // Only show error if it's an actual API failure, not empty data
      if (error.response?.status !== 404) {
        toast.error('Failed to load registered agents');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRevealCredential = async (providerId, field) => {
    try {
      const key = `${providerId}-${field}`;
      if (revealedCredentials[key]) {
        // Hide
        setRevealedCredentials(prev => ({ ...prev, [key]: null }));
      } else {
        // Reveal
        const response = await api.revealRACredential(providerId, field);
        setRevealedCredentials(prev => ({ ...prev, [key]: response.value }));
        toast.success(`${field} revealed (logged in audit trail)`, {
          icon: <Lock className="h-4 w-4" />
        });
      }
    } catch (error) {
      toast.error(`Failed to reveal ${field}`);
    }
  };

  const handleCopyCredential = (value, field) => {
    navigator.clipboard.writeText(value);
    toast.success(`${field} copied to clipboard`);
  };

  const toggleProvider = (providerId) => {
    const newExpanded = new Set(expandedProviders);
    if (newExpanded.has(providerId)) {
      newExpanded.delete(providerId);
    } else {
      newExpanded.add(providerId);
    }
    setExpandedProviders(newExpanded);
  };

  // Filter providers
  const filteredProviders = providers.filter(provider => {
    const matchesSearch = searchQuery === '' || 
      provider.provider_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (stateFilter === 'all') return matchesSearch;
    
    const hasState = provider.state_assignments?.some(a => a.state === stateFilter);
    return matchesSearch && hasState;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary/20 border-t-primary mx-auto"></div>
            <UserCheck className="h-8 w-8 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-muted-foreground font-medium">Loading registered agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto p-4 md:p-6 lg:p-8 space-y-6 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <UserCheck className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                Registered Agents
              </span>
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Central command for provider accounts and state coverage
            </p>
          </div>
          <Button 
            onClick={() => setAddProviderDialog(true)} 
            data-testid="add-provider-btn"
            className="w-full sm:w-auto shadow-lg"
            size="lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Provider
          </Button>
        </div>

        {/* Action Required Panel */}
        {overview && overview.missing_states_count > 0 && (
          <Card className="border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 via-amber-50/50 to-background dark:from-amber-950/50 dark:via-amber-950/20 dark:to-background shadow-lg">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg shrink-0">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
                </div>
                <div className="flex-1 space-y-2">
                  <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                    Action Required: {overview.missing_states_count} State{overview.missing_states_count !== 1 ? 's' : ''} Without Coverage
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {overview.missing_states.slice(0, 15).map(state => (
                      <Badge key={state} variant="outline" className="bg-background/50">
                        {state}
                      </Badge>
                    ))}
                    {overview.missing_states.length > 15 && (
                      <Badge variant="outline" className="bg-background/50">
                        +{overview.missing_states.length - 15} more
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overview Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-background dark:from-blue-950/20 dark:to-background border-blue-200/50 dark:border-blue-800/50 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2">
                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <Badge variant="secondary" className="text-xs">Total</Badge>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-blue-900 dark:text-blue-100">
                {overview?.total_providers || 0}
              </div>
              <div className="text-xs sm:text-sm font-medium text-blue-700/70 dark:text-blue-300/70 mt-1">
                Providers
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-background dark:from-green-950/20 dark:to-background border-green-200/50 dark:border-green-800/50 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2">
                <MapPin className="h-5 w-5 text-green-600 dark:text-green-400" />
                <Badge variant="secondary" className="text-xs">Coverage</Badge>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-green-900 dark:text-green-100">
                {overview?.total_states_covered || 0}
                <span className="text-lg text-muted-foreground">/50</span>
              </div>
              <div className="text-xs sm:text-sm font-medium text-green-700/70 dark:text-green-300/70 mt-1">
                States Active
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-background dark:from-purple-950/20 dark:to-background border-purple-200/50 dark:border-purple-800/50 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <Badge variant="secondary" className="text-xs">Annual</Badge>
              </div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-purple-900 dark:text-purple-100">
                ${overview?.total_annual_cost?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
              </div>
              <div className="text-xs sm:text-sm font-medium text-purple-700/70 dark:text-purple-300/70 mt-1">
                Total Cost
              </div>
            </CardContent>
          </Card>

          <Card className={`bg-gradient-to-br ${overview?.missing_states_count > 0 ? 'from-amber-50 to-background dark:from-amber-950/20' : 'from-gray-50 to-background dark:from-gray-950/20'} border-${overview?.missing_states_count > 0 ? 'amber' : 'gray'}-200/50 dark:border-${overview?.missing_states_count > 0 ? 'amber' : 'gray'}-800/50 shadow-lg hover:shadow-xl transition-shadow`}>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2">
                <AlertTriangle className={`h-5 w-5 ${overview?.missing_states_count > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`} />
                <Badge variant={overview?.missing_states_count > 0 ? "destructive" : "secondary"} className="text-xs">
                  {overview?.missing_states_count > 0 ? 'Alert' : 'OK'}
                </Badge>
              </div>
              <div className={`text-2xl sm:text-3xl font-bold ${overview?.missing_states_count > 0 ? 'text-amber-900 dark:text-amber-100' : 'text-muted-foreground'}`}>
                {overview?.missing_states_count || 0}
              </div>
              <div className={`text-xs sm:text-sm font-medium ${overview?.missing_states_count > 0 ? 'text-amber-700/70 dark:text-amber-300/70' : 'text-muted-foreground'} mt-1`}>
                Missing States
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="shadow-md">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex items-center gap-2 shrink-0">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium hidden sm:inline">Filters</span>
              </div>
              
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search providers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {overview?.assignments && 
                    [...new Set(overview.assignments.map(a => a.state))].sort().map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>

              <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
                {filteredProviders.length} of {providers.length} providers
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Providers List */}
        {filteredProviders.length === 0 ? (
          <Card className="shadow-md">
            <CardContent className="p-8 sm:p-12 text-center">
              <div className="max-w-md mx-auto space-y-4">
                <div className="p-4 bg-muted/50 rounded-full inline-block">
                  <UserCheck className="h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground/50" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">No Providers Found</h3>
                  <p className="text-sm text-muted-foreground">
                    {searchQuery || stateFilter !== 'all' 
                      ? 'Try adjusting your filters or search query.'
                      : 'Get started by adding your first registered agent provider.'}
                  </p>
                </div>
                {!searchQuery && stateFilter === 'all' && (
                  <Button onClick={() => setAddProviderDialog(true)} size="lg" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Provider
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredProviders.map((provider) => (
              <Card 
                key={provider.provider_id} 
                className="shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden"
              >
                <CardContent className="p-0">
                  {/* Provider Header */}
                  <div className="p-4 sm:p-6 bg-gradient-to-r from-muted/50 via-background to-background">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <button
                        onClick={() => toggleProvider(provider.provider_id)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors shrink-0"
                        aria-label="Toggle provider details"
                      >
                        {expandedProviders.has(provider.provider_id) ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                      </button>
                      
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="text-lg sm:text-xl font-bold truncate">
                            {provider.provider_name}
                          </h3>
                          <Badge 
                            variant={provider.status === 'active' ? 'default' : 'secondary'}
                            className="shrink-0"
                          >
                            {provider.status}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-4 w-4" />
                            <span className="font-medium">{provider.states_covered} states</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <DollarSign className="h-4 w-4" />
                            <span className="font-semibold text-foreground">
                              ${provider.total_annual_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr
                            </span>
                          </div>
                          {provider.contact_email && (
                            <div className="flex items-center gap-1.5">
                              <Mail className="h-4 w-4" />
                              <span className="truncate max-w-[200px]">{provider.contact_email}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full sm:w-auto">
                        {provider.login_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(provider.login_url, '_blank')}
                            className="flex-1 sm:flex-initial"
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            <span className="hidden sm:inline">Portal</span>
                            <span className="sm:hidden">Login</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Provider Details */}
                  {expandedProviders.has(provider.provider_id) && (
                    <div className="border-t bg-muted/20">
                      <div className="p-4 sm:p-6 space-y-4">
                        {/* Credentials Section */}
                        <div className="bg-gradient-to-br from-background via-background to-muted/50 rounded-lg p-4 sm:p-6 border shadow-sm">
                          <div className="flex items-center gap-2 mb-4">
                            <Lock className="h-5 w-5 text-primary" />
                            <h4 className="font-semibold text-base">Account Credentials</h4>
                            <Badge variant="outline" className="ml-auto text-xs">Encrypted</Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Username */}
                            <div className="space-y-2">
                              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                                <Mail className="h-3 w-3" />
                                Username
                              </Label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2.5 bg-muted/50 backdrop-blur rounded-lg text-sm font-mono border">
                                  {revealedCredentials[`${provider.provider_id}-username`] || '••••••••••••'}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevealCredential(provider.provider_id, 'username')}
                                  className="shrink-0"
                                >
                                  {revealedCredentials[`${provider.provider_id}-username`] ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </Button>
                                {revealedCredentials[`${provider.provider_id}-username`] && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopyCredential(
                                      revealedCredentials[`${provider.provider_id}-username`],
                                      'Username'
                                    )}
                                    className="shrink-0"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Password */}
                            <div className="space-y-2">
                              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                                <Lock className="h-3 w-3" />
                                Password
                              </Label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2.5 bg-muted/50 backdrop-blur rounded-lg text-sm font-mono border">
                                  {revealedCredentials[`${provider.provider_id}-password`] || '••••••••••••'}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevealCredential(provider.provider_id, 'password')}
                                  className="shrink-0"
                                >
                                  {revealedCredentials[`${provider.provider_id}-password`] ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </Button>
                                {revealedCredentials[`${provider.provider_id}-password`] && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopyCredential(
                                      revealedCredentials[`${provider.provider_id}-password`],
                                      'Password'
                                    )}
                                    className="shrink-0"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Site URL */}
                          {provider.website_url && (
                            <div className="mt-4 space-y-2">
                              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                                <Globe className="h-3 w-3" />
                                Site URL
                              </Label>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2.5 bg-muted/50 backdrop-blur rounded-lg text-sm font-mono border break-all">
                                  {provider.website_url}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(provider.website_url, '_blank', 'noopener,noreferrer')}
                                  className="shrink-0"
                                  title="Open in new tab"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCopyCredential(provider.website_url, 'Site URL')}
                                  className="shrink-0"
                                  title="Copy URL"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )}

                          {provider.mfa_notes && (
                            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <div className="flex items-start gap-2 text-sm">
                                <Lock className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                                <div>
                                  <span className="font-semibold text-blue-900 dark:text-blue-100">MFA Setup:</span>
                                  <span className="text-blue-700 dark:text-blue-300 ml-1">{provider.mfa_notes}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* State Assignments */}
                        {provider.state_assignments && provider.state_assignments.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-base flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-primary" />
                                State Coverage
                                <Badge variant="secondary">{provider.state_assignments.length}</Badge>
                              </h4>
                            </div>

                            {/* Mobile: Cards */}
                            <div className="block lg:hidden space-y-3">
                              {provider.state_assignments.map((assignment) => (
                                <Card key={assignment.assignment_id} className="shadow-sm">
                                  <CardContent className="p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                      <Badge variant="outline" className="text-base font-bold px-3 py-1">
                                        {assignment.state}
                                      </Badge>
                                      <Badge 
                                        variant={assignment.ra_status === 'active' ? 'default' : 'secondary'}
                                        className="text-xs"
                                      >
                                        {assignment.ra_status}
                                      </Badge>
                                    </div>
                                    
                                    {assignment.legal_entity_name && (
                                      <div className="flex items-start gap-2 text-sm">
                                        <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                                        <span className="text-foreground">{assignment.legal_entity_name}</span>
                                      </div>
                                    )}
                                    
                                    {assignment.virtual_address && (
                                      <div className="flex items-start gap-2 text-xs text-muted-foreground">
                                        <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                                        <div>
                                          <div>{assignment.virtual_address}</div>
                                          {assignment.city && (
                                            <div>{assignment.city}, {assignment.state_code} {assignment.zip}</div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div className="flex items-center justify-between pt-2 border-t">
                                      <span className="text-xs text-muted-foreground">Annual Fee</span>
                                      <span className="font-semibold text-base">
                                        ${assignment.annual_fee ? assignment.annual_fee.toFixed(2) : '0.00'}
                                      </span>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>

                            {/* Desktop: Table */}
                            <div className="hidden lg:block border rounded-lg overflow-hidden shadow-sm">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-muted/50 border-b">
                                    <tr>
                                      <th className="px-4 py-3 text-left font-semibold">State</th>
                                      <th className="px-4 py-3 text-left font-semibold">Entity</th>
                                      <th className="px-4 py-3 text-left font-semibold">Address</th>
                                      <th className="px-4 py-3 text-right font-semibold">Annual Fee</th>
                                      <th className="px-4 py-3 text-center font-semibold">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {provider.state_assignments.map((assignment) => (
                                      <tr 
                                        key={assignment.assignment_id} 
                                        className="hover:bg-muted/30 transition-colors"
                                      >
                                        <td className="px-4 py-3">
                                          <Badge variant="outline" className="font-mono font-bold">
                                            {assignment.state}
                                          </Badge>
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="flex items-center gap-2">
                                            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                            <span className="truncate max-w-[200px]">
                                              {assignment.legal_entity_name || 'N/A'}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                          {assignment.virtual_address ? (
                                            <div className="max-w-[250px]">
                                              <div>{assignment.virtual_address}</div>
                                              {assignment.city && (
                                                <div className="mt-0.5">
                                                  {assignment.city}, {assignment.state_code} {assignment.zip}
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <span>—</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          <span className="font-semibold tabular-nums">
                                            ${assignment.annual_fee ? assignment.annual_fee.toFixed(2) : '0.00'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <Badge 
                                            variant={assignment.ra_status === 'active' ? 'default' : 'secondary'}
                                            className="text-xs"
                                          >
                                            {assignment.ra_status === 'active' ? (
                                              <CheckCircle2 className="h-3 w-3 mr-1" />
                                            ) : (
                                              <Clock className="h-3 w-3 mr-1" />
                                            )}
                                            {assignment.ra_status}
                                          </Badge>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Provider Dialog */}
        <Dialog open={addProviderDialog} onOpenChange={setAddProviderDialog}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Add Registered Agent Provider
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const providerData = {
                provider_name: formData.get('provider_name'),
                contact_email: formData.get('contact_email'),
                contact_phone: formData.get('contact_phone') || null,
                website_url: formData.get('website_url') || null,
                username: formData.get('username') || null,
                password: formData.get('password') || null,
              };
              
              // Validate required fields
              if (!providerData.provider_name || !providerData.contact_email) {
                toast.error('Provider name and contact email are required');
                return;
              }
              
              // Call API to create provider
              api.createRAProvider(providerData)
                .then(() => {
                  toast.success('Provider created successfully');
                  setAddProviderDialog(false);
                  loadData(); // Reload provider list
                  e.target.reset();
                })
                .catch(error => {
                  console.error('Failed to create provider:', error);
                  toast.error(error.message || 'Failed to create provider');
                });
            }}>
              <div className="space-y-4 py-4">
                {/* Provider Information */}
                <div className="space-y-2">
                  <label htmlFor="provider_name" className="text-sm font-medium">
                    Provider Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="provider_name"
                    name="provider_name"
                    required
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g., Harvard Business Services, Inc."
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="contact_email" className="text-sm font-medium">
                    Contact Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    name="contact_email"
                    required
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g., contact@provider.com"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="contact_phone" className="text-sm font-medium">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    id="contact_phone"
                    name="contact_phone"
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g., (800) 555-0100"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="website_url" className="text-sm font-medium">
                    Website URL
                  </label>
                  <input
                    type="url"
                    id="website_url"
                    name="website_url"
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g., https://www.provider.com"
                  />
                </div>

                {/* Portal Credentials */}
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Portal Credentials (Optional)
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Credentials will be encrypted and stored securely
                  </p>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <label htmlFor="username" className="text-sm font-medium">
                        Username
                      </label>
                      <input
                        type="text"
                        id="username"
                        name="username"
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Portal username"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="password" className="text-sm font-medium">
                        Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Portal password"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setAddProviderDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Provider
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RegisteredAgentsManagement;
